import { Record } from 'immutable';

import { formatDuration } from './utils';
import { TIME_TYPE } from './constants';

// it can be either a Selenium or Appium command
const SeleniumCommandClass = Record({
  active: false,
  between_commands: 0,
  collapsed: true,
  duration: 0,
  hide_from_ui: false,
  HTTPStatus: 0,
  id: null,
  in_video_timeline: null,
  method: '',
  message: '', // used by wdio, contains test name, does not exist in nightwatch
  path: '',
  request: {},
  result: {},
  screenshot: null,
  screenshotUrl: null,
  statusCode: 0,
  start_time: null,
  suggestion_values: [],
  suggestion: null,
  timelineSeconds: 0,
  elementSelector: '', // internal, remapped selenium selector for more readable output
  performanceData: null,
  regressionData: null,
  rootCauseType: null,
  visual_command: false,
});

// Rename selenium methods
const methodsMap = {
  displayed: 'display',
};

/**
  * Selenium/WebDriver command record
  * @property {boolean} active
  * @property {number} between_commands
  * @property {boolean} collapsed
  * @property {number} duration
  * @property {boolean} hide_from_ui
  * @property {number} HTTPStatus
  * @property {*} id
  * @property {number} in_video_timeline
  * @property {string} method
  * @property {string} message
  * @property {string} path
  * @property {Object} request
  * @property {Object} result
  * @property {?string} screenshot
  * @property {?string} screenshotUrl
  * @property {number} statusCode
  * @property {number} start_time
  * @property {*[]} suggestion_values
  * @property {*} suggestion
  * @property {number} timelineSeconds
  * @property {string} elementSelector
  * @property {*} performanceData
  * @property {*} regressionData
  * @property {*} rootCauseType
  * @property {boolean} visual_command
  */
class SeleniumCommand extends SeleniumCommandClass {
  /**
   * Returns a stringified version of command's `request`.
   * @returns {string} Stringified JSON.
   */
  displayRequest() {
    return SeleniumCommand.print(this.get('request'));
  }

  /**
   * Returns a stringified version of command's `result`.
   * @returns {string} Stringified JSON.
   */
  displayResult() {
    return SeleniumCommand.print(this.get('result'));
  }

  /**
   * Returns command's `method` and `path`.
   * @returns {string}
   */
  displayPath() {
    return `${this.get('method')} ${this.get('path')}`;
  }

  /**
   * Returns command's `HTTPStatus` prefixed with "HTTP Status: ".
   * @returns {string}
   */
  displayHttpStatus() {
    return `HTTP Status: ${this.get('HTTPStatus')}`;
  }

  /**
   * Returns command's `duration` rounded to 2 decimal places.
   * @returns {string}
   */
  getDuration() {
    return SeleniumCommand.formatToFixed(this.get('duration'));
  }

  /**
   * @param {string} [field=]
   * @returns {number}
   */
  getTimeline(field = TIME_TYPE.TIMELINE) {
    if (field === TIME_TYPE.STARTTIME) {
      return this.get('start_time') * 1000;
    }

    return Number.isFinite(this.get('in_video_timeline')) ?
      this.get('in_video_timeline') :
      this.get('timelineSeconds');
  }

  /**
   * @returns {number}
   */
  getTimelineFormatted() {
    return formatDuration(this.getTimeline() * 1000);
  }

  /**
   * Determines whether the command failed.
   * @returns {boolean}
   */
  isFailed() {
    return this.get('statusCode') !== 0;
  }

  /**
   * Returns command's `screenshot`.
   * @returns {string}
   */
  getScreenshot() {
    return this.get('screenshot');
  }

  /**
   * Determines whether a screenshot is available for the command.
   * @returns {boolean}
   */
  hasScreenshot() {
    return this.getScreenshot() != null;
  }

  /**
   * @returns {string}
   */
  getSeleniumId() {
    return this.get('path').split('/')[1];
  }

  /**
   * @returns {string}
   */
  getSeleniumAction() {
    const method = this.get('path').split('/')[2];
    return methodsMap[method] || method;
  }

  /**
   * Returns a field from command's `request` object if available.
   * @param {string} field
   * @returns {?*}
   */
  getFromRequest(field) {
    return this.get('request') && this.get('request')[field];
  }

  /**
   * Try to return as useful a string as we can for the customers to understand what
   * the role of given command is.
   * @param {string} seleniumSelector
   * @returns {string}
   */
  getMethod(seleniumSelector) {
    /*
      We can't be never 100% confident that we cover all cases of Selenium protocol
      to make sure in case of missing scenario we won't block commands from rendering
      we will do fallback to known from the previous UI:
      "POST element/0.25562585637070323-1/displayed" value.
    */
    try {
      if (this.getFromRequest('url')) {
        return 'load url';
      }

      const path = this.get('path');
      if (path.includes('element')) {
        let method = this.get('method');

        if (method.toUpperCase() === 'POST') {
          if (/\/clear$/i.test(path)) {
            method = 'CLEAR';
          } else if (/\/click$/i.test(path)) {
            method = 'CLICK';
          } else if (/\/value$/i.test(path)) {
            method = 'SEND KEYS';
          } else {
            method = 'FIND';
          }
        }

        return method;
      }

      if (!seleniumSelector) {
        return this.get('method');
      }

      // try to return beauty titles only if we know elementSelector
      if (this.getFromRequest('using') === 'css selector') {
        return 'select';
      }
      if (this.getFromRequest('value')) {
        return 'value';
      }

      const seleniumAction = this.getSeleniumAction();
      if (seleniumAction) {
        return seleniumAction;
      }
    } catch (err) {
      return this.get('method');
    }

    return this.get('method');
  }

  /**
   * Returns a brief command's title to display in the UI.
   * @param {string} seleniumSelector
   * @returns {string}
   */
  getTitle(seleniumSelector) {
    /*
      We can't be never 100% confident that we cover all cases of Selenium protocol
      to make sure in case of missing scenario we won't block commands from rendering
      we will do fallback to known from the previous UI:
      "POST element/0.25562585637070323-1/displayed" value.
    */
    try {
      if (this.getFromRequest('url')) {
        return `${this.get('request').url}`;
      }

      if (this.getFromRequest('using') === 'css selector') {
        const value = this.getFromRequest('value');
        return (typeof value === 'string') ? value : JSON.stringify(value);
      }

      if (!seleniumSelector) {
        return this.get('path');
      }

      if (this.getFromRequest('value')) {
        return `${seleniumSelector} VALUE '${this.get('request').value.join('')}'`;
      }

      const seleniumAction = this.getSeleniumAction();
      if (seleniumAction) {
        return seleniumSelector;
      }
    } catch (err) {
      return this.get('path');
    }

    return this.get('path');
  }

  /**
   * Determines whether the command is a dummy command.
   * @returns {boolean}
   */
  isDummy() {
    return !(
      this.get('method') ||
      this.get('in_video_timeline') ||
      Object.keys(this.get('request')).length > 0 ||
      Object.keys(this.get('result')).length > 0
    );
  }
}

SeleniumCommand.print = (obj) => JSON.stringify(obj, null, '\t');
SeleniumCommand.formatToFixed = (number) => Number.parseFloat(number).toFixed(2);

/**
  * RDC command record
  * @augments SeleniumCommand
  */
class RdcJobCommand extends SeleniumCommand {
  /**
   * Returns command's `screenshotUrl`.
   * @returns {string}
   */
  getScreenshot() {
    return this.get('screenshotUrl');
  }

  // RDC has duration in ms while VDC has in sec
  // VDC has this task : https://saucedev.atlassian.net/browse/EMUSIM-3442 to make it in ms
  // we'll align it after both VDC and RDC have duration in ms

  /**
   * Returns command's `duration` rounded to 2 decimal places.
   * @returns {string}
   */
  getDuration() {
    return SeleniumCommand.formatToFixed(this.get('duration') / 1000);
  }
}

export default SeleniumCommand;
export {
  RdcJobCommand, SeleniumCommandClass, SeleniumCommand,
};
